import { __awaiter } from '../../_virtual/_tslib.js';
import nacl from 'tweetnacl';
import bs58 from 'bs58';
import { PublicKey } from '@solana/web3.js';
import { SolWalletConnector } from '../solWalletConnector.js';
import { buildUrl, decryptPayload, encryptPayload } from './utils.js';
import { storage, clearStorage } from './storage.js';

class PhantomRedirect extends SolWalletConnector {
    constructor(props) {
        super(Object.assign({}, props));
        this.name = 'Phantom';
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = storage.address.get();
            if (address) {
                return address;
            }
            yield this.connect();
            return undefined;
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            // Generate a new key pair
            const keyPair = nacl.box.keyPair();
            storage.encryptionPublicKey.set(keyPair.publicKey);
            storage.encryptionSecretKey.set(keyPair.secretKey);
            const isLocalHost = window.location.href.includes('localhost') ||
                window.location.href.includes('0.0.0.0') ||
                window.location.href.includes('127.0.0.1');
            const params = new URLSearchParams({
                app_url: isLocalHost ? 'https://demo.dynamic.xyz' : window.location.href,
                cluster: 'mainnet-beta',
                dapp_encryption_public_key: bs58.encode(keyPair.publicKey),
                redirect_link: window.location.href,
            });
            const url = buildUrl('connect', params);
            window.location.href = url;
        });
    }
    getSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const params = new URLSearchParams(window.location.search);
            const { data, nonce, phantom_encryption_public_key: phantomEncryptionPublicKey, encryptionSecretKey, } = this.getInputsOrThrow('getSession', ['data', 'nonce', 'phantom_encryption_public_key'], ['encryptionSecretKey']);
            const sharedSecret = nacl.box.before(bs58.decode(phantomEncryptionPublicKey), encryptionSecretKey);
            storage.sharedSecret.set(sharedSecret);
            const connectData = decryptPayload(data, nonce, sharedSecret);
            storage.session.set(connectData.session);
            storage.address.set(new PublicKey(connectData.public_key));
            params.delete('phantom_encryption_public_key');
            params.delete('data');
            params.delete('nonce');
            history.replaceState(null, '', `${window.location.origin}${window.location.pathname}?${params.toString()}`);
            return connectData.public_key;
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const { session, sharedSecret, encryptionPublicKey } = this.getInputsOrThrow('signMessage', [], ['session', 'sharedSecret', 'encryptionPublicKey']);
            storage.message.set(messageToSign);
            const payload = {
                message: bs58.encode(Buffer.from(messageToSign)),
                session,
            };
            const [nonce, encryptedPayload] = encryptPayload(payload, sharedSecret);
            const params = new URLSearchParams({
                dapp_encryption_public_key: bs58.encode(encryptionPublicKey),
                nonce: bs58.encode(nonce),
                payload: bs58.encode(encryptedPayload),
                redirect_link: window.location.href,
            });
            const url = buildUrl('signMessage', params);
            window.location.href = url;
            // throwing this to prevent local storage from being cleared.
            // when verifying signature, the SDK calls endSession when no
            // signature is returned. in the case of phantom mobile, a signature
            // is not returned from signMessage, so an error will always be thrown.
            // this is a workaround to prevent the SDK from clearing local storage
            // ideally we would figure out how to:
            //   1. kick off the sign message on one tab
            //   2. resume the process on that tab after the user signs in phantom
            throw new Error('ignore');
        });
    }
    extractSignature() {
        const params = new URLSearchParams(window.location.search);
        const { data, nonce, sharedSecret, message } = this.getInputsOrThrow('extractSignature', ['data', 'nonce'], ['sharedSecret', 'message']);
        const signMessageData = decryptPayload(data, nonce, sharedSecret);
        params.delete('data');
        params.delete('nonce');
        history.replaceState(null, '', `${window.location.origin}${window.location.pathname}?${params.toString()}`);
        return {
            message,
            signature: signMessageData.signature,
        };
    }
    getSigner() {
        return Promise.resolve(undefined);
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = storage.address.get();
            return address ? [address] : [];
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = storage.address.get();
            const session = storage.session.get();
            const sharedSecret = storage.sharedSecret.get();
            const encryptionPublicKey = storage.encryptionPublicKey.get();
            // Clear all local storage in case of any stale state
            clearStorage();
            // if there is no session/encrytion keys, then we don't need to disconnect
            if (!address || !session || !encryptionPublicKey || !sharedSecret)
                return;
            const payload = { session };
            const [nonce, encryptedPayload] = encryptPayload(payload, sharedSecret);
            const params = new URLSearchParams({
                dapp_encryption_public_key: bs58.encode(encryptionPublicKey),
                nonce: bs58.encode(nonce),
                payload: bs58.encode(encryptedPayload),
                redirect_link: window.location.href,
            });
            const url = buildUrl('disconnect', params);
            window.location.href = url;
        });
    }
    /**
     * Helper method to get inputs from query params and localstorage
     *
     * The second argument is used to read values from the query string
     *   e.g. ['data', 'nonce'] -> params.get('data') and params.get('nonce')
     *
     * The third argument is used to read values from local storage
     *   e.g. ['address', 'message'] -> storage.address.get() and storage.message.get()
     *
     * Throws an error if any of the inputs are unable to be found in their respective locations
     */
    getInputsOrThrow(methodName, queryParams, storageParams) {
        const inputs = {};
        const queryString = new URLSearchParams(window.location.search);
        queryParams.forEach((param) => {
            const value = queryString.get(param);
            if (!value) {
                throw new Error(`[PhantomRedirect] ${methodName} called, but required input '${param}' not found in query params`);
            }
            inputs[param] = value;
        });
        storageParams.forEach((storageParam) => {
            const value = storage[storageParam].get();
            if (!value) {
                throw new Error(`[PhantomRedirect] ${methodName} called, but required input '${storageParam}' not found in local storage`);
            }
            inputs[storageParam] = value;
        });
        return inputs;
    }
}

export { PhantomRedirect };
